import type { MatchChat, MatchChatMessage, PauseChatRow } from '@mogzo/game-ui';
import type { FrameChat, GameFrameHandle } from '@mogzo/play';
import type { ChatMessageView, MatchChatView } from '@mogzo/shared';
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, apiFetch } from './api';
import { type ChatLiveEvent, refreshChatCounts, useChatLive } from './chat';

const asLine = (m: ChatMessageView): MatchChatMessage => ({
  id: m.id,
  from: m.from.username,
  mine: m.mine,
  text: m.text,
  phrase: m.phrase,
  deleted: m.deleted,
});

/**
 * The chat of the match a game is playing (M38b), for MOGZO's chat over and beside the game:
 * loaded when the game starts or opens a match, kept current by the live connection, and each
 * quick phrase passed on to the game (`mogzo.chat.onPhrase`). Null while the game isn't in a
 * match whose mode has chat, and for players under 18.
 */
export function useMatchChat(
  game: RefObject<GameFrameHandle | null>,
  {
    enabled,
    openPanel,
    messages,
  }: { enabled: boolean; openPanel?: () => void; messages?: PauseChatRow | null },
) {
  const [matchId, setMatchId] = useState<string | null>(null);
  const [view, setView] = useState<MatchChatView | null>(null);
  const [reporting, setReporting] = useState<ChatMessageView | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  const load = useCallback(async (id: string) => {
    try {
      setView(await apiFetch<MatchChatView>(`/v1/chat/matches/${id}`));
    } catch {
      // No chat in this mode (or not allowed): nothing to show.
      setView(null);
    }
  }, []);

  useEffect(() => {
    setView(null);
    if (enabled && matchId) void load(matchId);
  }, [enabled, matchId, load]);

  const add = useCallback(
    (message: ChatMessageView) => {
      const current = viewRef.current;
      if (!current || message.conversationId !== current.conversationId) return;
      if (current.messages.some((m) => m.id === message.id)) return;
      setView({ ...current, messages: [...current.messages, message].slice(-100) });
      if (message.phrase) game.current?.phrase(message.from.username, message.text, message.mine);
    },
    [game],
  );

  const onLive = useCallback(
    (event: ChatLiveEvent) => {
      const current = viewRef.current;
      if (!current) return;
      if (event.type === 'message') add(event.message);
      else if (event.type === 'deleted' && event.conversationId === current.conversationId) {
        setView({
          ...current,
          messages: current.messages.map((m) =>
            m.id === event.messageId ? { ...m, deleted: true, text: '' } : m,
          ),
        });
      } else if (
        event.type === 'changed' &&
        (!event.conversationId || event.conversationId === current.conversationId) &&
        matchId
      ) {
        void load(matchId);
      }
    },
    [add, load, matchId],
  );
  useChatLive(enabled && Boolean(matchId), onLive);

  const send = useCallback(
    async (text: string, phrase: boolean): Promise<string | null> => {
      if (!matchId) return 'This match has no chat.';
      try {
        const { message } = await apiFetch<{ message: ChatMessageView }>(
          `/v1/chat/matches/${matchId}/messages`,
          { method: 'POST', body: JSON.stringify({ text, phrase }) },
        );
        add(message);
        refreshChatCounts();
        return null;
      } catch (error) {
        return error instanceof ApiError ? error.message : 'Could not send it. Try again.';
      }
    },
    [matchId, add],
  );

  const chat = useMemo<MatchChat | null>(
    () =>
      view
        ? {
            chat: view.chat,
            look: view.look,
            corner: view.corner,
            phrases: view.phrases,
            messages: view.messages.map(asLine),
            cantSend: view.cantSend,
            send,
            report: (id) => setReporting(view.messages.find((m) => m.id === id) ?? null),
          }
        : null,
    [view, send],
  );

  const frameChat = useMemo<FrameChat>(
    () => ({ chat, onMatch: setMatchId, openPanel, messages }),
    [chat, openPanel, messages],
  );

  return { chat, view, frameChat, send, reporting, setReporting };
}
